/**
 * ./src/modules/product/components/collection/CollectionRoomListGrid.tsx
 *
 * CollectionRoomListGrid component to display the content of the GetInspired page
 *
 * @copyright DesignShop, 2024
 * @since 08/19/2024
 */
import { createRef, useMemo, useState } from 'react';

import { HitType } from '@/modules/common/lib/abtastyStub';

import { useAddToCart } from '@/modules/cart/hooks/useAddToCart';
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { selectStatus } from '@/modules/cart/state/cartSlice';
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import { Heart } from '@/modules/common/components/icons';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import { useQueryParams } from '@/modules/common/hooks/useQueryParams';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { cn } from '@/modules/common/lib/utils';
import { SamplePrice } from '@/modules/product/components/sample/SamplePrice';
import { useVisibleItemsTracking } from '@/modules/product/hooks/useVisibleItemsTracking';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '@/modules/product/services/data-layer-events/utils';
import { SampleCard } from '@/modules/sample-card';
import {
  ShoppingListSaveType,
  useShoppingListActions,
} from '@/modules/shopping-list/hooks/useShoppingListActions';
import { selectShoppingListStatus } from '@/modules/shopping-list/state/shoppingListSlice';

import { useCollectionRoomSelector } from '../../context/CollectionRoomContext';
import useGridLayout from '../../hooks/useGridLayout';
import { useSampleCardVariants } from '../../hooks/useSampleCardVariants';

type SelectedProduct = {
  sku: string;
  color?: string;
  image?: {
    url?: string | null;
    label?: string | null;
  }[];
  price: number;
  productId: string;
  productName: string;
};

export const CollectionRoomListGrid = () => {
  const products = useCollectionRoomSelector('relatedProducts');
  const collection = useCollectionRoomSelector('collection');
  const { enhancedProducts } = useSampleCardVariants(products);

  const cartStatus = useAppSelector(selectStatus);
  const shoppingListStatus = useAppSelector(selectShoppingListStatus);
  const { sendFeatureFlagTracking } = useFeatureFlag();

  const [activeCategory] = useQueryParams<string>('c', 'All');
  const [sortPrice] = useQueryParams<string>('sort', '');
  const { gridLayout } = useGridLayout();
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const isMobile = useIsMobile('sm');
  const viewMode = gridLayout === 'side-by-side' && isMobile ? 'compact' : 'default';

  const { bulkAddProductsToShoppingList } = useShoppingListActions({
    bulkProducts: selectedProducts,
    type: ShoppingListSaveType.Product,
  });

  const totalPrice = selectedProducts.reduce((acc, product) => acc + product.price, 0);
  const productRefs = useMemo(() => products.map(() => createRef<HTMLDivElement>()), [products]);

  // Create filtered and sorted products array
  const filteredProducts = useMemo(() => {
    return enhancedProducts
      .filter((product) => {
        if (activeCategory === 'All') {
          return true;
        }
        const categoriesAvailable = product?.category?.name.split(' > ') || [];
        return categoriesAvailable.some((category) => category === activeCategory);
      })
      .sort((a, b) => {
        if (!sortPrice) {
          return 0;
        }
        const aPrice = a.samplePrice;
        const bPrice = b.samplePrice;
        return sortPrice === 'asc' ? aPrice - bPrice : bPrice - aPrice;
      });
  }, [enhancedProducts, activeCategory, sortPrice]);

  useVisibleItemsTracking({
    products: filteredProducts,
    productRefs,
    collection,
    shoppingListProducts: [],
    feature: DataLayerFeature.Collections,
    where: DataLayerWhere.SimilarItems,
    itemListId: collection.slug + '-similar',
    itemListName: collection.collectionName + ' - similar',
    itemListType: 'Room collection - similar',
  });

  const handleCheckProduct = (checked: boolean, sku: string) => {
    if (checked) {
      setSelectedProducts((prev) => {
        const product = products.find((product) => product.sku === sku);
        if (product) {
          return [
            ...prev,
            {
              sku,
              color: product.color || '',
              image:
                product.images?.map((image) => ({
                  url: image.url,
                  label: image.label,
                })) || [],
              price: product.samplePrice || 0,
              productId: product.id,
              productName: product.name || '',
            },
          ];
        }
        return prev;
      });
    } else {
      setSelectedProducts((prev) => prev.filter((product) => product.sku !== sku));
    }
  };

  const handleAddToWishlist = async () => {
    await bulkAddProductsToShoppingList(false);

    addToWishlistDataLayerEvent({
      feature: DataLayerFeature.Collections,
      where: DataLayerWhere.SimilarItems,
      method: 'Multi-select',
      type: ShoppingListSaveType.Collection,
      shoppingListProducts: null,
      product: {
        slug: collection.slug ?? '',
        title: collection.collectionName ?? '',
      } satisfies CollectionDataLayer,
      item_list_type: 'Room collection - similar',
      item_list_name: collection.collectionName + ' - similar',
      item_list_id: collection.slug + '-similar',
    });
    setSelectedProducts([]);

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Product Saved',
      label: collection.collectionName,
    });
  };

  const { onAddToCartBulk } = useAddToCart({
    where: DataLayerWhere.SimilarItems,
  });

  const handleAddProductsToCart = async () => {
    const productsToAdd = products.filter((product) =>
      selectedProducts.some((selectedProduct) => selectedProduct.sku === product.sku)
    );

    onAddToCartBulk(productsToAdd.map((product) => product));
    setSelectedProducts([]);
  };

  if (products.length === 0) {
    return <div className="px-8">No products</div>;
  }

  return (
    <div
      className={cn(
        'grid h-fit gap-2 md:grid-cols-[repeat(auto-fill,minmax(185px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(262px,1fr))]',
        {
          'grid-cols-1': gridLayout === 'thumbnail',
          'grid-cols-2': gridLayout === 'side-by-side',
        }
      )}
    >
      {filteredProducts.map((product, index) => {
        return (
          <div
            key={`${product.sku}-${product.published}`}
            ref={productRefs[index]}
            data-product-id={product.id}
            data-index={index}
            className="flex flex-col"
          >
            <SampleCard
              index={index}
              key={product.sku}
              where={DataLayerWhere.Collections}
              isSelectable={product.isOnStock && !!product.pricePerUnit}
              isSelected={selectedProducts.some(
                (selectedProduct) => selectedProduct.sku === product?.sku
              )}
              onCheck={(checked: boolean) => handleCheckProduct(checked, product?.sku || '')}
              {...product}
              feature={DataLayerFeature.Collections}
              item_list_type="Room collection - similar"
              item_list_name={collection.collectionName + ' - similar'}
              item_list_id={collection.slug + '-similar'}
              {...(collection.slug && {
                hrefQueryParams: {
                  collection: collection.slug + '-similar',
                },
              })}
              data-testid="sample-card"
              viewMode={viewMode}
            />
          </div>
        );
      })}

      <div
        className={`animate-fade-in-up visible fixed bottom-5 left-0 right-0 z-10 mx-auto flex w-fit items-center gap-3 rounded-xxl border border-gray-200 bg-white p-4 opacity-100 transition-all duration-100 ease-in-out ${selectedProducts.length === 0 ? 'invisible translate-y-full opacity-0' : ''} `}
      >
        <Button
          className="py-3 max-lg:p-3"
          variant="secondary"
          onClick={handleAddToWishlist}
          isLoading={shoppingListStatus === 'loading'}
          data-testid="add-to-wishlist-button"
        >
          <span className="hidden gap-2.5 lg:flex">
            <Heart size="small" /> Save
          </span>
          <span className="lg:hidden">
            <Heart />
          </span>
        </Button>
        <Button
          className="relative flex gap-2"
          onClick={handleAddProductsToCart}
          isLoading={cartStatus === 'loading'}
          data-testid="add-to-cart-button"
        >
          {selectedProducts
            .slice(0, 3)
            .map(
              (product, index) =>
                product?.image?.[0]?.url && (
                  <Image
                    key={product.sku}
                    src={product.image[0].url}
                    alt="product"
                    className={`size-[25px] rounded-md border border-transparent z-[${index}] ${index !== 0 ? '-ml-5 border-black' : ''} `}
                    width={25}
                    height={25}
                  />
                )
            )}{' '}
          Add to cart <SamplePrice priceInCents={totalPrice} />
        </Button>
      </div>
    </div>
  );
};
